import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, Package, Send, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [forwarding, setForwarding] = useState({});

  const handleForwardToCJ = async (orderId) => {
    setForwarding((s) => ({ ...s, [orderId]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${API}/admin/orders/${orderId}/forward-cj`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.duplicate) {
        toast.info('Already forwarded to CJ');
      } else {
        toast.success(`CJ order created: ${res.data.cj_order_id}`);
      }
      await fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CJ forward failed');
    } finally {
      setForwarding((s) => ({ ...s, [orderId]: false }));
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailDialog(true);
  };

  const handleStatusChange = async (orderId, newStatus, tracking = null, reason = null) => {
    try {
      const token = localStorage.getItem('admin_token');
      const payload = { status: newStatus };
      if (tracking) payload.tracking_number = tracking;
      if (reason) payload.cancel_reason = reason;
      
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Order ${newStatus}. Email sent to customer.`);
      fetchOrders();
      if (selectedOrder && selectedOrder.order_id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-green-500/10 text-green-500';
      case 'shipped':
        return 'bg-blue-500/10 text-blue-500';
      case 'delivered':
        return 'bg-purple-500/10 text-purple-500';
      case 'cancelled':
        return 'bg-red-500/10 text-red-500';
      default:
        return 'bg-orange-500/10 text-orange-500';
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 md:p-8" data-testid="admin-orders">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Orders</h1>
          <p className="text-muted-foreground">Manage customer orders and shipping</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No orders yet</p>
          </div>
        ) : (
          <div className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-semibold text-foreground">Order ID</th>
                    <th className="text-left p-4 font-semibold text-foreground">Customer</th>
                    <th className="text-left p-4 font-semibold text-foreground">Total</th>
                    <th className="text-left p-4 font-semibold text-foreground">Status</th>
                    <th className="text-left p-4 font-semibold text-foreground">CJ</th>
                    <th className="text-left p-4 font-semibold text-foreground">Date</th>
                    <th className="text-right p-4 font-semibold text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.order_id} className="border-t border-border hover:bg-muted/50">
                      <td className="p-4">
                        <p className="font-mono text-sm text-foreground">
                          #{order.order_id.substring(0, 8).toUpperCase()}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="font-medium text-foreground">{order.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                      </td>
                      <td className="p-4 text-foreground font-semibold">${order.total_amount.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="p-4">
                        {order.cj_order_id ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-500/10 text-green-500" title={order.cj_order_id} data-testid={`cj-status-${order.order_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Sent
                          </span>
                        ) : order.cj_status === 'failed' ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-500/10 text-red-500" title={order.cj_error}>
                            <XCircle className="w-3 h-3" /> Failed
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {!order.cj_order_id && (
                            <button
                              onClick={() => handleForwardToCJ(order.order_id)}
                              disabled={forwarding[order.order_id]}
                              title="Forward to CJ Dropshipping"
                              data-testid={`forward-cj-${order.order_id}`}
                              className="p-2 rounded-lg hover:bg-accent/20 text-accent transition-colors disabled:opacity-50"
                            >
                              {forwarding[order.order_id] ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : (
                                <Send className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleViewDetails(order)}
                            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Order Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedOrder && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading font-bold text-foreground">
                    Order Details
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Order Info */}
                  <div className="p-4 rounded-xl bg-muted">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Order ID</p>
                        <p className="font-mono font-semibold text-foreground">
                          #{selectedOrder.order_id.substring(0, 8).toUpperCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Date</p>
                        <p className="font-semibold text-foreground">
                          {new Date(selectedOrder.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Customer Information</h3>
                    <div className="p-4 rounded-xl bg-muted space-y-2">
                      <p className="text-foreground">{selectedOrder.customer_name}</p>
                      <p className="text-muted-foreground">{selectedOrder.customer_email}</p>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  {selectedOrder.shipping_address && (
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-3">Shipping Address</h3>
                      <div className="p-4 rounded-xl bg-muted space-y-1 text-muted-foreground">
                        <p className="text-foreground font-medium">
                          {selectedOrder.shipping_address.firstName} {selectedOrder.shipping_address.lastName}
                        </p>
                        <p>{selectedOrder.shipping_address.streetAddress}</p>
                        {selectedOrder.shipping_address.apartment && (
                          <p>{selectedOrder.shipping_address.apartment}</p>
                        )}
                        <p>
                          {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.postalCode}
                        </p>
                        <p>{selectedOrder.shipping_address.country}</p>
                        <p className="pt-2">{selectedOrder.shipping_address.phone}</p>
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Order Items</h3>
                    <div className="space-y-3">
                      {selectedOrder.items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center p-4 rounded-xl bg-muted">
                          <div>
                            <p className="font-medium text-foreground">{item.product_name}</p>
                            <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                          </div>
                          <p className="font-semibold text-accent">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
                      <span className="text-lg font-semibold text-foreground">Total</span>
                      <span className="text-2xl font-bold text-accent">${selectedOrder.total_amount.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Status Update */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Update Order Status</h3>
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-3">
                        {['pending', 'paid', 'accepted', 'shipped', 'delivered', 'cancelled'].map((status) => (
                          <Button
                            key={status}
                            onClick={() => {
                              if (status === 'shipped' || status === 'cancelled') {
                                // Show input dialog for tracking/reason
                                setStatusAction(status);
                              } else {
                                handleStatusChange(selectedOrder.order_id, status);
                              }
                            }}
                            variant={selectedOrder.status === status ? 'default' : 'outline'}
                            className={selectedOrder.status === status ? 'bg-accent text-accent-foreground' : ''}
                          >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Button>
                        ))}
                      </div>
                      
                      {/* Tracking Number Input */}
                      {statusAction === 'shipped' && (
                        <div className="p-4 rounded-xl bg-muted space-y-3">
                          <Label htmlFor="tracking">Tracking Number (Optional)</Label>
                          <Input
                            id="tracking"
                            value={trackingNumber}
                            onChange={(e) => setTrackingNumber(e.target.value)}
                            placeholder="Enter tracking number"
                            className="bg-input border-border text-foreground"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={() => {
                                handleStatusChange(selectedOrder.order_id, 'shipped', trackingNumber);
                                setStatusAction(null);
                                setTrackingNumber('');
                              }}
                              className="bg-accent text-accent-foreground"
                            >
                              Mark as Shipped
                            </Button>
                            <Button variant="outline" onClick={() => setStatusAction(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Cancel Reason Input */}
                      {statusAction === 'cancelled' && (
                        <div className="p-4 rounded-xl bg-muted space-y-3">
                          <Label htmlFor="cancel-reason">Cancel Reason</Label>
                          <textarea
                            id="cancel-reason"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Enter reason for cancellation"
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg bg-input border border-border text-foreground"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={() => {
                                handleStatusChange(selectedOrder.order_id, 'cancelled', null, cancelReason);
                                setStatusAction(null);
                                setCancelReason('');
                              }}
                              className="bg-red-500 text-white hover:bg-red-600"
                            >
                              Cancel Order
                            </Button>
                            <Button variant="outline" onClick={() => setStatusAction(null)}>
                              Back
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
